import { useAuth } from "./lib/useAuth";
import { useHashRoute } from "./lib/route";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { ContactPage } from "./pages/ContactPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PortfolioPage } from "./pages/PortfolioPage";
import { BookingsPage } from "./pages/BookingsPage";
import { BookingDetailPage } from "./pages/BookingDetailPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { GalleriesPage } from "./pages/GalleriesPage";
import { GalleryDetailPage } from "./pages/GalleryDetailPage";
import { LeadsPage } from "./pages/LeadsPage";
import { PagesPage } from "./pages/PagesPage";
import { PageEditorPage } from "./pages/PageEditorPage";
import { PackagesPage } from "./pages/PackagesPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { InvoiceSettingsPage } from "./pages/InvoiceSettingsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { AdminPage } from "./pages/AdminPage";
import { AccountPage } from "./pages/AccountPage";
import { Shell } from "./components/Shell";
import { CmsShell } from "./components/cms/CmsShell";
import { PWAInstallBanner } from "./components/PWAInstallBanner";

// Strip query string for route matching.
function stripQuery(r: string): string {
  const i = r.indexOf("?");
  return i >= 0 ? r.slice(0, i) : r;
}

// Strip /app prefix so inner logic stays clean.
function appPath(path: string): string {
  if (path.startsWith("/app")) return path.slice(4) || "/";
  return path;
}

export function App() {
  const auth = useAuth();
  const route = useHashRoute(); // returns pathname+search, e.g. "/app/bookings"

  if (auth.loading) {
    return <div className="auth-page" aria-busy="true" />;
  }

  const fullPath = stripQuery(route);

  if (!auth.isAuthenticated) {
    if (fullPath === "/app/login" || fullPath === "/login") return (<><LoginPage /><PWAInstallBanner /></>);
    if (fullPath === "/app/contact" || fullPath === "/contact") return (<><ContactPage /><PWAInstallBanner /></>);
    return (<><LandingPage /><PWAInstallBanner /></>);
  }

  // All admin routes are under /app/; strip prefix for matching.
  const path = appPath(fullPath);

  let page;
  let pageTitle: string | undefined;

  if (path.startsWith("/portfolio")) {
    page = <PortfolioPage />;
    pageTitle = "Website Settings";
  } else if (path.startsWith("/bookings/")) {
    const bookingId = decodeURIComponent(path.slice("/bookings/".length));
    page = bookingId ? <BookingDetailPage id={bookingId} /> : <BookingsPage />;
  } else if (path.startsWith("/bookings")) {
    page = <BookingsPage />;
  } else if (path.startsWith("/clients/")) {
    const clientId = decodeURIComponent(path.slice("/clients/".length));
    page = clientId ? <ClientDetailPage clientId={clientId} /> : <ClientsPage />;
  } else if (path.startsWith("/clients")) {
    page = <ClientsPage />;
  } else if (path.startsWith("/galleries/")) {
    const slug = decodeURIComponent(path.slice("/galleries/".length));
    page = slug ? <GalleryDetailPage slug={slug} /> : <GalleriesPage />;
  } else if (path.startsWith("/galleries")) {
    page = <GalleriesPage />;
  } else if (path.startsWith("/pages/")) {
    const pageId = decodeURIComponent(path.slice("/pages/".length));
    page = pageId ? <PageEditorPage pageId={pageId} /> : <PagesPage />;
    // pageTitle for page editor is resolved inside CmsShell via API
  } else if (path.startsWith("/pages")) {
    page = <PagesPage />;
    pageTitle = "Pages";
  } else if (path.startsWith("/leads")) {
    page = <LeadsPage />;
  } else if (path.startsWith("/packages")) {
    page = <PackagesPage />;
  } else if (path.startsWith("/invoices/")) {
    const invoiceId = decodeURIComponent(path.slice("/invoices/".length));
    page = invoiceId ? <InvoiceDetailPage id={invoiceId} /> : <InvoicesPage />;
  } else if (path.startsWith("/invoices")) {
    page = <InvoicesPage />;
  } else if (path === "/invoice-settings") {
    page = <InvoiceSettingsPage />;
  } else if (path.startsWith("/analytics")) {
    page = <AnalyticsPage />;
  } else if (path.startsWith("/admin")) {
    page = <AdminPage />;
  } else if (path.startsWith("/account")) {
    page = <AccountPage />;
  } else {
    page = <DashboardPage />;
  }

  // CMS routes get the two-panel CmsShell layout.
  if (path.startsWith("/pages") || path.startsWith("/portfolio")) {
    return (
      <>
        <CmsShell route={route} user={auth.user!} onLogout={auth.logout} pageTitle={pageTitle}>
          {page}
        </CmsShell>
        <PWAInstallBanner />
      </>
    );
  }

  return (
    <>
      <Shell route={route} user={auth.user!} onLogout={auth.logout} fullWidth={path.startsWith("/bookings") && !path.startsWith("/bookings/")}>
        {page}
      </Shell>
      <PWAInstallBanner />
    </>
  );
}
